import {
  AlertTriangle,
  AlertCircle,
  Save,
  ShieldCheck,
  Loader2,
  CheckCircle,
  Download,
} from 'lucide-react';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageHeader } from '@/components/layout/PageContainer';
import { DcoBadge } from '@/components/DcoBadge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldIssue } from '../types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  'ratio_items.1x1': 'Feed Assets (1:1)',
  'ratio_items.4x5': 'Feed Assets (4:5)',
  // Backend pseudo-ratio: either slot satisfies it, so it is named for the pair.
  'ratio_items.feed': 'Feed Assets (1:1 or 4:5)',
  'ratio_items.9x16': 'Story/Reel Assets (9:16)',
  primary_text: 'Primary Text',
  destination_url: 'Destination URL',
  asset_files: 'Assets',
  carousel_items: 'Carousel Cards',
};

const SITELINK_SUBFIELD: Record<string, string> = {
  title: 'Title',
  description1: 'Description 1',
  description2: 'Description 2',
  final_url: 'Final URL',
};

function formatFieldName(field: string): string {
  if (FIELD_DISPLAY_NAMES[field]) return FIELD_DISPLAY_NAMES[field];
  // Indexed sitelink fields: "sitelinks[0].title" → "Sitelink 1 Title"
  const sl = field.match(/^sitelinks\[(\d+)\]\.(.+)$/);
  if (sl) {
    const label =
      SITELINK_SUBFIELD[sl[2]] ?? sl[2].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `Sitelink ${parseInt(sl[1], 10) + 1} ${label}`;
  }
  // Per-card indexed fields: "headline.2" → "Headline (Card 3)"
  const dotIndexed = field.match(/^([a-z_]+)\.(\d+)$/);
  if (dotIndexed) {
    const base =
      FIELD_DISPLAY_NAMES[dotIndexed[1]] ??
      dotIndexed[1].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${base} (Card ${parseInt(dotIndexed[2]) + 1})`;
  }
  // Search ad indexed fields: "headline_0" → "Headline 1", "long_headline_1" → "Long Headline 2"
  const underscoreIndexed = field.match(/^(.+?)_(\d+)$/);
  if (underscoreIndexed) {
    const base = underscoreIndexed[1].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${base} ${parseInt(underscoreIndexed[2]) + 1}`;
  }
  // Media slot fields: "media_landscape_image" → "Landscape Image"
  if (field.startsWith('media_')) {
    return field
      .slice(6)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Group field keys into sections for structured tooltip display. */
function getFieldSection(field: string): string {
  if (field.startsWith('headline') || field.startsWith('long_headline') || field === 'headlines')
    return 'Headlines';
  if (field.startsWith('description') || field === 'descriptions') return 'Descriptions';
  if (field.startsWith('display_url')) return 'Display URL';
  if (field === 'business_name') return 'Business Name';
  if (field === 'final_url') return 'Final URL';
  if (field === 'cta') return 'CTA';
  if (
    field.startsWith('media_') ||
    field.startsWith('youtube_video') ||
    field.startsWith('image_extension')
  )
    return 'Media';
  if (field.startsWith('sitelinks')) return 'Sitelinks';
  return 'Other';
}

function IssueTooltipContent({
  fieldIssues,
  kind,
}: {
  fieldIssues: Record<string, FieldIssue>;
  kind: 'errors' | 'warnings';
}) {
  const entries = Object.entries(fieldIssues)
    .map(([field, issue]) => ({ field, messages: issue[kind], section: getFieldSection(field) }))
    .filter((e) => e.messages.length > 0);

  if (entries.length === 0) return null;

  // Group by section — flatten all messages under section header
  const grouped: Record<string, string[]> = {};
  for (const entry of entries) {
    if (!grouped[entry.section]) grouped[entry.section] = [];
    for (const msg of entry.messages) {
      // For indexed fields, prepend the field name (e.g. "Headline 2: Exceeds 30 character limit")
      const fieldName = formatFieldName(entry.field);
      const isSameAsSection =
        fieldName.toLowerCase().replace(/\s/g, '') ===
        entry.section.toLowerCase().replace(/\s/g, '');
      grouped[entry.section].push(isSameAsSection ? msg : `${fieldName}: ${msg}`);
    }
  }

  return (
    <div className="max-h-64 overflow-y-auto space-y-3 min-w-52 max-w-xs">
      {Object.entries(grouped).map(([section, messages]) => (
        <div key={section}>
          <p
            className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${kind === 'errors' ? 'text-red-600' : 'text-amber-600'}`}
          >
            {section}
          </p>
          <ul className="space-y-0.5 pl-2">
            {messages.map((msg, i) => (
              <li key={i} className="text-xs text-slate-600 leading-snug flex gap-1.5">
                <span
                  className={`mt-0.5 shrink-0 ${kind === 'errors' ? 'text-red-400' : 'text-amber-400'}`}
                >
                  •
                </span>
                {msg}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AssetPreviewHeaderProps {
  campaignId?: string;
  campaignSubtitle?: string;
  /** DCO campaigns follow a different flow — shows the DCO badge next to the title */
  isDco?: boolean;
  errorCount: number;
  warningCount: number;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
  onShowErrorsChange?: (v: boolean) => void;
  showWarnings: boolean;
  onShowWarningsChange: (v: boolean) => void;
  /** Hide Save/Save & Validate — use on the format selection screen */
  showActions?: boolean;
  onSave?: () => void;
  onSaveAndValidate?: () => void;
  /** Greyed/disabled when false — no unsaved changes */
  isDirty?: boolean;
  /** Shows spinner and disables buttons while a save is in flight */
  isSaving?: boolean;
  /** Shows spinner on Validate button while validation is in flight */
  isValidating?: boolean;
  /** Whether the user has validated this variant (enables Approve when no errors) */
  hasValidated?: boolean;
  /** Whether there are any unresolved errors from the last validation */
  hasErrors?: boolean;
  /** Whether the current variant is already approved — shows locked badge */
  isApproved?: boolean;
  /** Shows spinner on Approve button while approval is in flight */
  isApproving?: boolean;
  /** Called when the Approve button is clicked (opens modal in parent) */
  onApprove?: () => void;
  /** When true, hide all editing action buttons (Save, Validate, Approve) */
  readOnly?: boolean;
  /** When true, hide Save and Validate but keep Approve visible (locked versions) */
  isLocked?: boolean;
  /** Called when the Download button is clicked */
  onDownload?: () => void;
}

export function AssetPreviewHeader({
  campaignId,
  campaignSubtitle,
  isDco = false,
  errorCount,
  warningCount,
  fieldIssues = {},
  showWarnings,
  onShowWarningsChange,
  showActions = true,
  onSave,
  onSaveAndValidate,
  isDirty = false,
  isSaving = false,
  isValidating = false,
  hasValidated = false,
  hasErrors = false,
  isApproved = false,
  isApproving = false,
  onApprove,
  readOnly = false,
  isLocked = false,
  onDownload,
}: AssetPreviewHeaderProps) {
  const btnBase =
    'px-2.5 py-1 rounded-lg text-sm outline-none flex items-center gap-1.5 whitespace-nowrap shrink-0 transition-all active:scale-[0.97] border';
  const busy = isSaving || isValidating || isApproving;

  const approveTooltip = isApproved
    ? 'Approved — save changes to re-approve'
    : !hasValidated
      ? isLocked
        ? 'Validation is unavailable for locked versions'
        : 'Validate before approving'
      : hasErrors
        ? 'Fix all errors before approving'
        : 'Ready to approve';

  const actions = (
    <>
      {isDco && <DcoBadge />}
      <div className="flex-1" />

      {showActions && errorCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`${btnBase} bg-red-100 text-red-500 border-red-100 cursor-default`}>
              <AlertCircle className="w-4 h-4" />
              {errorCount} Error{errorCount !== 1 ? 's' : ''}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="bg-white border border-slate-200 shadow-lg p-3 rounded-xl"
            arrowClassName="fill-white"
          >
            <IssueTooltipContent fieldIssues={fieldIssues} kind="errors" />
          </TooltipContent>
        </Tooltip>
      )}

      {showActions && warningCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onShowWarningsChange(!showWarnings)}
              className={`${btnBase} ${showWarnings ? 'bg-amber-100 text-amber-500 border-amber-100 hover:bg-amber-200' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}
            >
              <AlertTriangle className="w-4 h-4" />
              {warningCount} Warning{warningCount !== 1 ? 's' : ''}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="bg-white border border-slate-200 shadow-lg p-3 rounded-xl"
            arrowClassName="fill-white"
          >
            <IssueTooltipContent fieldIssues={fieldIssues} kind="warnings" />
          </TooltipContent>
        </Tooltip>
      )}

      {/* Validate — secondary style, shown when editable, saves first if dirty */}
      {showActions && !readOnly && !isLocked && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onSaveAndValidate}
              disabled={busy}
              className={`${btnBase} bg-white border-slate-300 hover:border-slate-400 hover:shadow-sm ${busy ? 'text-slate-400 cursor-default' : 'text-slate-700'}`}
            >
              {isValidating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              <span>Validate</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {`Validate against platform specs${isDirty ? ' — saves first' : ''}`}
          </TooltipContent>
        </Tooltip>
      )}

      {showActions && onDownload && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onDownload}
              className={`${btnBase} bg-white border-slate-300 hover:border-slate-400 hover:shadow-sm text-slate-700`}
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Download all assets</TooltipContent>
        </Tooltip>
      )}

      {/* Save — blue when dirty, grey when clean */}
      {showActions && !readOnly && !isLocked && (
        <button
          onClick={onSave}
          disabled={!isDirty || busy}
          className={`${btnBase} ${
            isDirty && !busy
              ? 'bg-[#0014cc] text-white border-[#0014cc] hover:bg-[#0014cc]/90'
              : 'bg-white text-slate-400 border-slate-200 cursor-default'
          }`}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>Save</span>
        </button>
      )}

      {/* Approve — shown when editable, enabled only after clean validation */}
      {showActions && !readOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={hasValidated && !hasErrors && !isApproved && !busy ? onApprove : undefined}
              disabled={busy}
              className={`${btnBase} ${
                isApproved
                  ? 'bg-green-100 text-green-600 border-green-100 cursor-default'
                  : hasValidated && !hasErrors && !busy
                    ? 'bg-green-100 text-green-600 border-green-100 hover:bg-green-200'
                    : 'bg-white text-slate-300 border-slate-200 cursor-default'
              }`}
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span>{isApproved ? 'Approved' : 'Approve'}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{approveTooltip}</TooltipContent>
        </Tooltip>
      )}
    </>
  );

  return (
    <PageHeader>
      <Breadcrumb actions={actions} title={campaignId} suffix={campaignSubtitle} sidebarLocked />
    </PageHeader>
  );
}
